import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Search, Banknote, MapPin } from 'lucide-react';
import { fetchJobs, updateProfile } from '../services/api';
import { useApp } from '../context/AppContext';
import LocationInput from '../components/LocationInput';
import { jobTitle } from '../constants/jobOptions';
import './MapPage.css';

// Schlüssel sind die job_type-Slugs des Backends
const JOB_COLORS = {
  aushilfe:       '#f97316',
  praktikum:      '#3b82f6',
  schulpraktikum: '#60a5fa',
  ausbildung:     '#8b5cf6',
  duales_studium: '#a855f7',
  werkstudent:    '#06b6d4',
  vollzeit:       '#22c55e',
};

function jobColor(type) { return JOB_COLORS[type] || '#6b7280'; }

// Ein Icon-Objekt pro Firma + Jobart statt pro Render: ein neues Objekt lässt
// react-leaflet das Marker-DOM bei jedem Render (Tippen, Regler, Nachladen) neu bauen.
const iconCache = new Map();
function companyIcon(name, type) {
  const key = `${name}|${type}`;
  if (!iconCache.has(key)) iconCache.set(key, buildCompanyIcon(name, type));
  return iconCache.get(key);
}

function buildCompanyIcon(name, type) {
  const initials = (name || 'JO').slice(0, 2).toUpperCase();
  const bg = jobColor(type);
  return L.divIcon({
    className: '',
    html: `<div style="position:relative;width:36px;height:44px;display:flex;flex-direction:column;align-items:center">
      <div style="width:36px;height:36px;background:${bg};border-radius:50%;border:2.5px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center">
        <span style="font-size:11px;font-weight:800;color:#fff;font-family:Inter,sans-serif;letter-spacing:-0.3px">${initials}</span>
      </div>
      <div style="width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:9px solid ${bg}"></div>
    </div>`,
    iconSize:    [36, 44],
    iconAnchor:  [18, 44],
    popupAnchor: [0, -44],
  });
}

// Wohnort-Marker
const homeIcon = L.divIcon({
  className: '',
  html: `<div style="width:40px;height:40px;background:#2563eb;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 10px rgba(37,99,235,0.5);display:flex;align-items:center;justify-content:center;font-size:18px">🏠</div>`,
  iconSize:   [40, 40],
  iconAnchor: [20, 20],
});

// Client-Fallback-Geocoder (nur nötig für die manuelle Ortssuche und für
// Datensätze ohne DB-Koordinaten — reguläre Daten kommen geocodet vom Server)
const geocodeCache = {};
async function geocode(query) {
  if (geocodeCache[query] !== undefined) return geocodeCache[query];
  const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1&countrycodes=de`;
  const res = await fetch(url, { headers: { 'Accept-Language': 'de' } });
  const data = await res.json();
  const result = data?.[0]
    ? { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) }
    : null;
  geocodeCache[query] = result;
  return result;
}

function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371, d2r = Math.PI / 180;
  const dLat = (lat2 - lat1) * d2r;
  const dLng = (lng2 - lng1) * d2r;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1 * d2r) * Math.cos(lat2 * d2r) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Zoom so wählen, dass der äußere Ring ins Bild passt
function zoomForRadius(km) {
  if (km <= 5) return 12;
  if (km <= 10) return 11;
  if (km <= 25) return 10;
  return 9;
}

// Nur fliegen, wenn sich Mittelpunkt oder Zoomstufe wirklich ändern. Sonst lief
// beim Öffnen und bei jedem Regler-Schritt eine 0,8-s-Animation, die gegen
// Ziehen und Zoomen arbeitet (Ruckeln).
function FlyTo({ lat, lng, radius }) {
  const map = useMap();
  useEffect(() => {
    const zoom = zoomForRadius(radius);
    if (map.getZoom() === zoom && map.getCenter().equals([lat, lng])) return;
    map.flyTo([lat, lng], zoom, { duration: 0.8 });
  }, [lat, lng, radius, map]);
  return null;
}

const BUFFER_KM = 5;

// Kreise: feste Objekte, sonst setzt react-leaflet bei jedem Render den Stil neu
const RADIUS_STYLE = { color: '#2563eb', fillColor: '#3b82f6', fillOpacity: 0.08, weight: 2 };
const BUFFER_STYLE = { color: '#2563eb', fill: false, opacity: 0.35, weight: 1.5, dashArray: '6 6' };

// Trackpad (Mac): Leaflet springt pro Wisch-Geste sonst mindestens eine ganze
// Zoomstufe und startet die nächste Animation, bevor die letzte fertig ist.
// ponytail: Kalibrierknopf – fühlt es sich zu träge an, WHEEL_PX_PER_ZOOM senken.
const ZOOM_SNAP = 0.5;
const WHEEL_PX_PER_ZOOM = 120;

// Kacheln nur von MapTiler; der Key wird beim Build eingesetzt (REACT_APP_MAPTILER_KEY).
// Ohne Key zeigt die Seite einen Hinweis statt fremder Fehlerkacheln.
// 512er-Kacheln mit zoomOffset -1: gleiche Schärfe, ein Viertel der Requests.
const MAPTILER_KEY = process.env.REACT_APP_MAPTILER_KEY;
const TILE_URL = `https://api.maptiler.com/maps/streets-v2/{z}/{x}/{y}.png?key=${MAPTILER_KEY}`;
const TILE_ATTR = '<a href="https://www.maptiler.com/copyright/" target="_blank" rel="noreferrer">&copy; MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">&copy; OpenStreetMap contributors</a>';

export default function MapPage() {
  const { user, userType } = useApp();

  // Wohnort + Umkreis aus dem Profil (Setup); Koordinaten kommen aus der DB
  const profileCoords = user?.youth_lat != null && user?.youth_lng != null
    ? { lat: user.youth_lat, lng: user.youth_lng }
    : null;

  const [userCoords, setUserCoords] = useState(profileCoords);
  const [radius, setRadius] = useState(user?.max_distance_km || 10);
  const [rawJobs, setRawJobs] = useState([]);
  const [geoJobs, setGeoJobs] = useState([]);
  const [locationInput, setLocationInput] = useState('');
  const [searching, setSearching] = useState(false);
  const saveTimer = useRef(null);

  // Fallback: Profil hat einen Ort, aber (noch) keine Koordinaten → einmal geocoden
  useEffect(() => {
    if (userCoords || !user?.youth_location) return;
    geocode(user.youth_location + ', Deutschland')
      .then(c => c && setUserCoords(c))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.youth_location]);

  // Profil nachgeladen (z.B. Session-Restore) → Wohnort übernehmen
  useEffect(() => {
    if (!userCoords && profileCoords) setUserCoords(profileCoords);
    if (user?.max_distance_km) setRadius(user.max_distance_km);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.youth_lat, user?.max_distance_km]);

  // Jobs laden, sobald ein Standort feststeht
  useEffect(() => {
    if (!userCoords) return;
    fetchJobs({ limit: 100 })
      .then(({ data }) => {
        const list = (Array.isArray(data) ? data : data?.jobs || data?.results || [])
          .filter(j => j.location);
        setRawJobs(list);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!userCoords]);

  // Jobs mit DB-Koordinaten sofort anzeigen; Rest lazy per Nominatim nachladen
  useEffect(() => {
    if (!rawJobs.length) return;
    // pos einmal anlegen: gleiche Array-Referenz = kein setLatLng bei jedem Render
    setGeoJobs(rawJobs.filter(j => j.lat != null && j.lng != null).map(j => ({ ...j, pos: [j.lat, j.lng] })));

    // "Remote"-Jobs haben keinen geocodierbaren Ort
    const missing = rawJobs.filter(j => j.lat == null && !/remote|home\s*office/i.test(j.location));
    if (!missing.length) return;
    let cancelled = false;
    (async () => {
      for (const job of missing) {
        if (cancelled) return;
        const key = job.location + ', Deutschland';
        const isCached = geocodeCache[key] !== undefined;
        try {
          const c = await geocode(key);
          if (c && !cancelled) {
            setGeoJobs(prev =>
              prev.some(j => j.id === job.id) ? prev : [...prev, { ...job, lat: c.lat, lng: c.lng, pos: [c.lat, c.lng] }]
            );
          }
        } catch {}
        if (!isCached && !cancelled) {
          await new Promise(r => setTimeout(r, 1100)); // Nominatim-Rate-Limit
        }
      }
    })();
    return () => { cancelled = true; };
  }, [rawJobs]);

  // Aus der Vorschlagsliste gewählt: Koordinaten stehen schon in der DB
  const handlePickLocation = (text, loc) => {
    setLocationInput(text);
    if (loc?.lat != null && loc?.lng != null) {
      setUserCoords({ lat: Number(loc.lat), lng: Number(loc.lng) });
    }
  };

  // Freitext ohne Auswahl (Enter/Button) → Fallback über Nominatim
  const handleSearch = async () => {
    if (!locationInput.trim() || searching) return;
    setSearching(true);
    try {
      const c = await geocode(locationInput.trim() + ', Deutschland');
      if (c) setUserCoords(c);
    } catch {}
    setSearching(false);
  };

  // Slider: Kreis/Filter live, Profil-Rückschreiben entprellt
  const handleRadiusChange = (r) => {
    setRadius(r);
    if (user && userType === 'youth') {
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        updateProfile({ max_distance: `${r} km` }).catch(() => {});
      }, 1200);
    }
  };
  useEffect(() => () => clearTimeout(saveTimer.current), []);

  // Filter inkl. Pufferzone; Entfernung einmal pro Job berechnen
  const visibleJobs = useMemo(() => (userCoords
    ? geoJobs
        .map(j => ({ ...j, dist: haversine(userCoords.lat, userCoords.lng, j.lat, j.lng) }))
        .filter(j => j.dist <= radius + BUFFER_KM)
    : []), [geoJobs, userCoords, radius]);

  const center = useMemo(() => userCoords && [userCoords.lat, userCoords.lng], [userCoords]);

  // Marker nur neu bauen, wenn sich Jobs oder Umkreis ändern – nicht beim Tippen
  // im Suchfeld oder anderen Renders der Seite
  const jobMarkers = useMemo(() => visibleJobs.map(job => (
    <Marker
      key={job.id}
      position={job.pos}
      icon={companyIcon(job.company_name, job.job_type)}
      opacity={job.dist > radius ? 0.45 : 1}
    >
      <Popup>
        <div className="map-popup">
          <strong className="map-popup__title">{jobTitle(job.title)}</strong>
          <span className="map-popup__company">
            {job.company_name} · {Math.round(job.dist)} km entfernt
            {job.dist > radius ? ' (knapp außerhalb)' : ''}
          </span>
          {job.salary && (
            <span className="map-popup__salary"><Banknote size={12} /> {job.salary}</span>
          )}
          <a href={`/jobs/${job.id}`} className="map-popup__btn">
            Mehr Infos →
          </a>
        </div>
      </Popup>
    </Marker>
  )), [visibleJobs, radius]);

  if (!MAPTILER_KEY) {
    return (
      <div className="map-page map-page--empty">
        <div className="map-empty">
          <MapPin size={40} strokeWidth={1.4} className="map-empty__icon" />
          <h2>Karte gerade nicht verfügbar</h2>
          <p>Die Kartenansicht ist im Moment nicht erreichbar. Jobs in deiner Nähe findest du solange über die Suche.</p>
        </div>
      </div>
    );
  }

  // Kein Wohnort im Profil und noch nichts gesucht → Ort manuell wählen
  if (!userCoords) {
    return (
      <div className="map-page map-page--empty">
        <div className="map-empty">
          <MapPin size={40} strokeWidth={1.4} className="map-empty__icon" />
          <h2>Jobs auf der Karte</h2>
          <p>
            {user?.youth_location
              ? 'Dein Wohnort wird geladen…'
              : 'Gib deinen Ort ein, um Jobs in deiner Nähe zu finden.'}
          </p>
          <div className="map-empty__search">
            <LocationInput
              value={locationInput}
              onChange={handlePickLocation}
              inputClassName="map-empty__input"
              placeholder="Ort eingeben, z.B. Kaiserslautern"
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              autoFocus
            />
            <button className="map-empty__btn" onClick={handleSearch} disabled={searching}>
              {searching ? '…' : <Search size={18} />}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="map-page">
      <div className="map-search">
        <LocationInput
          value={locationInput}
          onChange={handlePickLocation}
          inputClassName="map-search__input"
          placeholder="Anderen Ort suchen..."
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
        />
        <button className="map-search__btn" onClick={handleSearch} disabled={searching}>
          {searching ? '…' : <Search size={16} />}
        </button>
      </div>

      <div className="map-container">
        <MapContainer
          center={center}
          zoom={zoomForRadius(radius)}
          zoomSnap={ZOOM_SNAP}
          wheelPxPerZoomLevel={WHEEL_PX_PER_ZOOM}
          style={{ height: '100%', width: '100%' }}
          zoomControl
        >
          <TileLayer
            url={TILE_URL}
            attribution={TILE_ATTR}
            tileSize={512}
            zoomOffset={-1}
            maxZoom={20}
            minZoom={1}
            updateWhenZooming={false}
            crossOrigin
          />

          <FlyTo lat={userCoords.lat} lng={userCoords.lng} radius={radius} />

          {/* Wohnort */}
          <Marker position={center} icon={homeIcon}>
            <Popup>
              <div className="map-popup">
                <strong className="map-popup__title">Dein Standort</strong>
                {user?.youth_location && (
                  <span className="map-popup__company">{user.youth_location}</span>
                )}
              </div>
            </Popup>
          </Marker>

          {/* Innerer Kreis: gewählter Umkreis */}
          <Circle center={center} radius={radius * 1000} pathOptions={RADIUS_STYLE} />
          {/* Äußerer Ring: Pufferzone (+5 km), dezent gestrichelt */}
          <Circle center={center} radius={(radius + BUFFER_KM) * 1000} pathOptions={BUFFER_STYLE} />

          {jobMarkers}
        </MapContainer>
      </div>

      {/* Umkreis-Regler */}
      <div className="map-radius">
        <span className="map-radius__label">Umkreis</span>
        <input
          type="range"
          className="map-radius__slider"
          min={5}
          max={50}
          step={5}
          value={radius}
          onChange={e => handleRadiusChange(parseInt(e.target.value))}
          aria-label="Umkreis in Kilometern"
        />
        <span className="map-radius__value">{radius} km</span>
      </div>
    </div>
  );
}
